import React from 'react';
import { Box, Tabs, Tab, Typography } from '@mui/material';
import RefillSaleSummary from '../features/refilsale/RefillSaleSummary';
import RefillUpdate from '../features/refilsale/RefillUpdate';
import RefillHistory from '../features/refilsale/RefillHistory';
import BarChartIcon from '@mui/icons-material/BarChart';
import EditNoteIcon from '@mui/icons-material/EditNote';
import HistoryIcon from '@mui/icons-material/History';
import { PRIMARY } from 'themes/colors';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  [key: string]: any;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`refill-tabpanel-${index}`}
      aria-labelledby={`refill-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `refill-tab-${index}`,
    'aria-controls': `refill-tabpanel-${index}`,
  };
}

const tabs = [
  { label: 'View Summary', icon: <BarChartIcon fontSize="small" /> },
  { label: 'Refill Update', icon: <EditNoteIcon fontSize="small" /> },
  { label: 'Refill History', icon: <HistoryIcon fontSize="small" /> },
];

const RefilSale: React.FC = () => {
  const [value, setValue] = React.useState(0);

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: 4 }}>
      {/* Page Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${PRIMARY.darker} 0%, ${PRIMARY.dark} 50%, ${PRIMARY.main} 100%)`,
          color: 'white',
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 3, md: 4 },
          borderRadius: 1,
        }}
      >
        <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
          Refill Sale Management
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          Track, record and review refill sale transactions
        </Typography>
      </Box>

      {/* Sticky Tab Bar */}
      <Box
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="refill sale tabs"
          sx={{
            px: { xs: 1, sm: 3 },
            '& .MuiTabs-indicator': {
              bgcolor: 'primary.main',
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          {tabs.map((tab, idx) => (
            <Tab
              key={tab.label}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
              {...a11yProps(idx)}
              sx={{
                fontWeight: 600,
                fontSize: { xs: 12, sm: 14 },
                minHeight: 56,
                textTransform: 'none',
                color: 'text.secondary',
                '&.Mui-selected': { color: 'primary.main' },
              }}
            />
          ))}
        </Tabs>
      </Box>

      <TabPanel value={value} index={0}>
        <RefillSaleSummary />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <RefillUpdate />
      </TabPanel>
      <TabPanel value={value} index={2}>
        <RefillHistory />
      </TabPanel>
    </Box>
  );
};

export default RefilSale;
